/**
 * @lines98/shared/config/content-routes — ЕДИНЫЙ источник URL-каркаса контент-
 * страниц (origin-relative) + парсер пути. ADR-0020 «единый per-route продукт» +
 * ADR-0021 addendum-001 «URL-модель B» (английский = корень БЕЗ префикса).
 *
 * ЗАЧЕМ / WHY THIS EXISTS: до этого мини-SSG строил ВНУТРЕННИЕ ссылки рейла /
 * крошек / nav абсолютным боевым доменом (`https://colorlines98.online/en/...`),
 * а игра-SPA и контент жили на РАЗНЫХ origin (5173 vs 8080) — owner: «два разных
 * сайта», ссылки на localhost не работали. Этот модуль даёт ОДНУ функцию пути
 * `contentPath(lang, slug)` (origin-relative) и обратный парсер `parseContentPath`.
 * И SSG (краулер-зеркало), и клиент-SPA импортируют ЭТОТ источник → пути совпадают
 * побайтно. Абсолютный URL (canonical / OG / hreflang / sitemap для краулера)
 * собирается отдельно из `PUBLIC_BASE_URL` — ТОЛЬКО там, где нужен абсолютный
 * адрес, НЕ во внутренней навигации.
 *
 * URL-МОДЕЛЬ B (ADR-0021 addendum-001 §A, owner 2026-06-19): английский живёт
 * ТОЛЬКО в корне БЕЗ языкового сегмента (`/`, `/rules/`); остальные языки — с
 * префиксом (`/ru/`, `/ru/rules/`). `en` в корне = самый «весомый» URL для
 * English-first международного сайта. `/en/...` запрошен явно → 301 на корне-
 * вариант (`/en/rules/` → `/rules/`); en-дубля под `/en/` нет.
 *
 * SCOPE: origin-relative построение/парсинг путей контента (модель B), нормализация
 *   slug, `absoluteUrl(base, lang, slug)` для краулерских мета-полей, канонизация
 *   избыточного `/en/`-префикса (`canonicalEnRedirect`), build-time гард коллизии
 *   slug↔код языка (`assertNoSlugLangCollision`). Vanilla, без DOM.
 * NOT IN SCOPE: тексты страниц (content/<lang>/*.md), HTML-шаблон (SSG template),
 *   роутинг игровых экранов (lib/game-routing), бренд/домен/языки (./seo.ts).
 *
 * Cross-references: ADR-0020 §«Ссылки — origin-relative»; ADR-0021 addendum-001
 *   §A (модель B) + §A.1 (коллизия slug↔язык); ./seo.ts (UI_LANGS / CONTENT_LANGS /
 *   DEFAULT_CONTENT_LANG); ./balance.ts PUBLIC_BASE_URL (canonical).
 */
import { DEFAULT_CONTENT_LANG, UI_LANGS, } from './seo.js';
/**
 * Origin-relative путь контент-страницы (URL-модель B, ADR-0021 addendum-001 §A):
 *   - английский (`DEFAULT_CONTENT_LANG`) — БЕЗ языкового префикса:
 *       лендинг (slug === '') → `/`
 *       страница             → `/<slug>/`
 *   - остальные языки — с префиксом:
 *       лендинг → `/<lang>/`
 *       страница → `/<lang>/<slug>/`
 * ВСЕГДА с ведущим и замыкающим «/» (канон путей контента; SSG пишет каталог,
 * сервер его раздаёт). Origin НЕ добавляется — для внутренней навигации (рейл /
 * крошки / nav / CTA). Абсолютный адрес — `absoluteUrl()`. Принимает любой код
 * `UI_LANGS` (вкл. языки-заглушки `/de/…` — у них есть путь, но контент на
 * en-фолбэке; SEO-защита — noindex + canonical→корень, T-I18N-9).
 */
export function contentPath(lang, slug) {
    const s = normalizeSlug(slug);
    // Английский = корень без сегмента языка (модель B).
    if (lang === DEFAULT_CONTENT_LANG) {
        return s === '' ? '/' : `/${s}/`;
    }
    // Остальные языки — с префиксом.
    return s === '' ? `/${lang}/` : `/${lang}/${s}/`;
}
/**
 * Абсолютный URL контент-страницы (для canonical / OG / hreflang / sitemap —
 * краулерские мета-поля, которым нужен полный адрес). `base` = `PUBLIC_BASE_URL`
 * без замыкающего «/». Внутренняя навигация ЭТО НЕ использует (см. `contentPath`).
 * Под модель B: en-лендинг → `${base}/`, en-страница → `${base}/<slug>/`.
 */
export function absoluteUrl(base, lang, slug) {
    return `${base.replace(/\/+$/, '')}${contentPath(lang, slug)}`;
}
/**
 * Разобрать pathname (origin-relative) в `{ lang, slug }` по URL-модели B
 * (ADR-0021 addendum-001 §A):
 *   - первый сегмент ∈ `UI_LANGS` → это язык, остаток — slug
 *     (`/ru/rules` → `{ lang:'ru', slug:'rules' }`, `/de/` → `{ lang:'de', slug:'' }`);
 *   - первый сегмент НЕ язык (или путь = `/`) → язык = `en`, ВЕСЬ путь — slug
 *     (`/rules` → `{ lang:'en', slug:'rules' }`; `/` → `{ lang:'en', slug:'' }`).
 * Возвращает `lang` из `UI_LANGS` (включая заглушки). НИКОГДА не возвращает null —
 * любой путь контентен под моделью B (en = корень). Caller сам решает, отдать ли
 * путь игре/реплею/oauth (см. classify в lib/content-routing / game-routing).
 * Терпим к замыкающему «/» и к его отсутствию. Игнорирует query/hash.
 *
 * ⚠ Раз en живёт в корне, ЛЮБОЙ не-языковой путь (вкл. игровые `/play`, `/daily`,
 * `/r/<id>`) распарсится как en-slug — поэтому caller'ы (boot-роутинг) проверяют
 * игровые/реплей-пути ДО контентного парсинга (порядок в app.ts/classifyBootPath).
 */
export function parseContentPath(pathname) {
    const segs = pathname.replace(/^\/+|\/+$/g, '').split('/').filter(Boolean);
    if (segs.length === 0) {
        // Корень `/` → en-лендинг (модель B: en = корень).
        return { lang: DEFAULT_CONTENT_LANG, slug: '' };
    }
    const first = segs[0];
    if (isUiLangCode(first)) {
        // Первый сегмент — код языка: остаток = slug.
        return { lang: first, slug: normalizeSlug(segs.slice(1).join('/')) };
    }
    // Первый сегмент НЕ язык → en + весь путь как slug.
    return { lang: DEFAULT_CONTENT_LANG, slug: normalizeSlug(segs.join('/')) };
}
/** Тип-гард: строка — один из 12 кодов `UI_LANGS`. */
function isUiLangCode(s) {
    return UI_LANGS.includes(s);
}
/**
 * Является ли pathname избыточным `/en/`-префиксом, который надо КАНОНИЗИРОВАТЬ
 * 301-редиректом на корне-вариант (ADR-0021 addendum-001 §A): английский живёт
 * ТОЛЬКО в корне, поэтому `/en/` → `/`, `/en/rules/` → `/rules/`. Возвращает
 * корне-относительный целевой путь редиректа, либо null (путь НЕ под `/en/`, не
 * требует канонизации). 301 (постоянная канонизация структуры URL) — реализует
 * серверный/прод-слой (Caddy) и клиентский boot (replaceState).
 */
export function canonicalEnRedirect(pathname) {
    const segs = pathname.replace(/^\/+|\/+$/g, '').split('/').filter(Boolean);
    if (segs[0] !== DEFAULT_CONTENT_LANG)
        return null;
    const slug = normalizeSlug(segs.slice(1).join('/'));
    // Корне-вариант en (без префикса) — цель 301.
    return contentPath(DEFAULT_CONTENT_LANG, slug);
}
/**
 * Является ли pathname корнем сайта (`/` или пусто). Под моделью B корень = en-
 * лендинг (контент, НЕ редирект — addendum-001 §A/§E). Сохранено для caller'ов,
 * которым важно отличить «голый корень» (особый boot-кейс редиректа по языку
 * браузера) от обычной en-страницы.
 */
export function isSiteRoot(pathname) {
    return pathname === '' || pathname === '/';
}
/**
 * Путь лендинга дефолт-языка (en) — под моделью B это КОРЕНЬ `/` (был `/en/`).
 * Используется как fallback-цель навигации к домашней en-странице.
 */
export function defaultLandingPath() {
    return contentPath(DEFAULT_CONTENT_LANG, '');
}
/** Снять обрамляющие «/» со slug (склейка путей — единообразно тут). */
export function normalizeSlug(slug) {
    return slug.replace(/^\/+|\/+$/g, '');
}
/**
 * Build-time гард коллизии «контент-slug vs код языка» (ADR-0021 addendum-001 §A.1):
 * под моделью B первый сегмент пути решает «язык или slug», поэтому ни один контент-
 * slug не должен совпадать с кодом из `UI_LANGS` (иначе `/it/` спорил бы — Italiano-
 * лендинг ИЛИ страница `it`). Сейчас пересечение пусто (verified addendum §A.1); этот
 * гард ловит будущую коллизию в момент добавления страницы (мини-SSG зовёт на сборке),
 * а не в проде. Бросает с явной ошибкой при коллизии. `slugs` — все `page.slug`
 * (лендинг slug '' — не код языка, безопасен).
 */
export function assertNoSlugLangCollision(slugs) {
    const langs = new Set(UI_LANGS);
    for (const slug of slugs) {
        const s = normalizeSlug(slug);
        if (s !== '' && langs.has(s)) {
            throw new Error(`URL-модель B (ADR-0021 addendum §A.1): контент-slug «${s}» совпадает с кодом ` +
                `языка из UI_LANGS — первый сегмент пути стал бы неоднозначным (язык vs страница). ` +
                `Переименуй страницу.`);
        }
    }
}
//# sourceMappingURL=content-routes.js.map